"use client";

import { FEATURE_FLAGS } from ".constants/featureFlags";
import { isEmpty, map } from "lodash";
import { useTranslations } from "next-intl";
import { FC, useMemo } from "react";

import { AiRequest } from "@/.gql/graphql";
import {
  PhotoSettings,
  useUndressContext,
  useUndressSettingsContext,
} from "@/providers";

import { useRenderItem } from "./useRenderItem";

/**
 * Компонент списка настроек (одежда, позы, сеты).
 * Рендерит список секций с настройками для выбора одежды, поз и сетов.
 * Поддерживает динамическое обновление `sectionsRef` при изменении массивов данных.
 *
 * @component
 * @example
 * return (
 *   <MainSettingsList />
 * )
 */
export const MainSettingsList: FC = () => {
  const t = useTranslations("HomePage.settingsPopup");
  const { clickSettings, selectedCollectionCount } = useUndressContext();
  const {
    wrapperRef,
    sectionsRef,
    handleScroll,
    costumes,
    poses,
    sets,
    storylines,
    clips,
  } = useUndressSettingsContext();

  const { renderFilterItems, userPromt } = useRenderItem();

  const { costume, pose, set, storyline, clip } = clickSettings;

  /**
   * Функция для установки ссылки (ref) на секцию с настройками.
   *
   * @param {number} index - Индекс секции.
   * @returns {function} Функция, которая устанавливает ref на элемент секции.
   */
  const setSectionRef = (index: number) => (el: HTMLDivElement | null) => {
    if (el) {
      sectionsRef.current.set(index, el);
    }
  };

  /**
   * Обновляет `sectionsRef` при изменении массивов данных (`costumes`, `poses`, `sets`).
   * Очищает старые данные в `sectionsRef` и добавляет новые, если массивы не пустые.
   */
  // useEffect(() => {
  //   // Обновление ссылок на секции при изменении массивов
  //   sectionsRef.current.clear();

  //   if (!isEmpty(costumes)) {
  //     sectionsRef.current.set(0, null);
  //   }
  //   if (!isEmpty(poses)) {
  //     sectionsRef.current.set(1, null);
  //   }
  //   if (!isEmpty(sets)) {
  //     sectionsRef.current.set(2, null);
  //   }
  // }, [costumes, poses, sets, sectionsRef]);

  // console.log("sectionsRef: ", sectionsRef.current);

  /**
   * Рендерит секцию настроек (одежда, позы, сеты).
   *
   * @param {number} index - Индекс секции.
   * @param {AiRequest[]} items - Данные для рендера.
   * @param {string | number} selectedItem - Текущее выбранное значение.
   * @param {keyof PhotoSettings} itemType - Тип настройки (`costume`, `pose`, `set`).
   * @param {AiRequest[]} [extraItems=[]] - Дополнительные элементы (например, `noSelectedItem`).
   * @returns {JSX.Element | null} Секция настроек или null, если данные пустые.
   */
  const renderSection = (
    index: number,
    items: AiRequest[],
    selectedItem: string | number,
    itemType: keyof PhotoSettings,
    extraItems: AiRequest[] = [],
    title?: string,
  ) =>
    !isEmpty(items) && (
      <div
        key={index}
        ref={setSectionRef(index)}
        className="flex flex-col gap-2"
      >
        {title && (
          <span className="text-md font-normal text-balance">{title}</span>
        )}

        <div className="grid grid-cols-4 gap-2">
          {renderFilterItems([...extraItems, ...items], selectedItem, itemType)}
        </div>
      </div>
    );

  /**
   * Тип для элемента списка настроек.
   * Описывает структуру данных для рендера секции настроек (одежда, позы, сеты).
   *
   * @typedef {Object} ListItem
   * @property {AiRequest[]} items - Массив элементов для отображения (костюмы, позы или сеты)
   * @property {string | number} selectedItem - Идентификатор выбранного элемента
   * @property {keyof PhotoSettings} itemType - Тип секции ('costume' | 'pose' | 'set')
   * @property {AiRequest[]} extraItems - Дополнительные элементы (например, кнопка "prompt")
   * @property {string} [title] - Опциональный заголовок секции
   */
  type SettingsListProps = {
    items: AiRequest[];
    selectedItem: string | number;
    itemType: keyof PhotoSettings;
    extraItems: AiRequest[];
    title?: string;
  }[];

  /**
   * Мемоизированный список секций настроек.
   * Формирует массив объектов для рендера секций в зависимости от AB-теста.
   *
   * @type {List}
   */
  const settingsList: SettingsListProps = useMemo(
    () =>
      [
        FEATURE_FLAGS.IS_CLIPS_ENABLED &&
          !isEmpty(clips) && {
            items: clips,
            selectedItem: clip,
            itemType: "clip",
            extraItems: [],
            // title: t("settingsStorylinesNote"),
          },
        !isEmpty(costumes) && {
          items: costumes,
          selectedItem: costume,
          itemType: "costume",
          extraItems: [userPromt(t("prompt"), 0)],
        },
        !isEmpty(poses) && {
          items: poses,
          selectedItem: pose,
          itemType: "pose",
          extraItems: [],
        },
        !isEmpty(sets) && {
          items: sets,
          selectedItem: set,
          itemType: "set",
          extraItems: [],
          title: t("settingsSetsNote", { count: selectedCollectionCount || 9 }),
        },
        !isEmpty(storylines) && {
          items: storylines,
          selectedItem: storyline,
          itemType: "storyline",
          extraItems: [],
          title: t("settingsStorylinesNote"),
        },
      ].filter(Boolean) as SettingsListProps,
    [
      clips,
      clip,
      costumes,
      costume,
      userPromt,
      t,
      poses,
      pose,
      sets,
      set,
      selectedCollectionCount,
      storylines,
      storyline,
    ],
  );

  return (
    <div
      ref={wrapperRef}
      className="scroll-mask-y-4 scrollbar-y-2 flex flex-col gap-indent overflow-x-hidden"
      onScroll={handleScroll}
    >
      {map(
        settingsList,
        ({ items, selectedItem, itemType, extraItems, title }, Index) =>
          renderSection(
            Index,
            items,
            selectedItem,
            itemType,
            extraItems,
            title,
          ),
      )}
    </div>
  );
};
